import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, Alert, Link as MuiLink,
} from '@mui/material';
import { VideoCall } from '@mui/icons-material';
import { interviewPanelService } from '../services/api';
import LiveKitRoomView from './LiveKitRoomView';

interface PanelMember {
  userId: number;
  fullName: string;
}

interface MyPanelInterview {
  id: number;
  candidateName: string;
  position: string;
  resumeUrl: string | null;
  scheduledAt: string;
  durationMinutes: number;
  status: string;
  panelMembers: PanelMember[];
  canJoin: boolean;
}

const statusColor = (status: string): 'default' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'Scheduled': return 'success';
    case 'Cancelled': return 'error';
    case 'NoShow': return 'warning';
    default: return 'default';
  }
};

// Mirrors InterviewScheduling.tsx's formatIst — the API returns IST wall-clock strings, this
// avoids the browser re-interpreting them through its own local timezone.
const formatIst = (value: string) => {
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) return value;
  const [y, m, d] = datePart.split('-');
  const [hh, mm] = timePart.split(':');
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const dateLabel = date.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  let hour = Number(hh);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${dateLabel}, ${hour}:${mm} ${suffix} IST`;
};

// This is the "interview panel" side of the feature — every employee (any role) who has been
// added to an interview's panel sees it here, whether or not they're HR/Admin. Nothing shows up
// until HR/Admin actually schedules an interview and picks them as a panelist (see
// InterviewScheduling.tsx / IInterviewSchedulingService.GetMyPanelInterviewsAsync).
const MyInterviewPanel: React.FC = () => {
  const [interviews, setInterviews] = useState<MyPanelInterview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<{ interviewId: number; token: string; serverUrl: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInterviews(await interviewPanelService.getMine());
    } catch {
      setError('Could not load your interview panel assignments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async (interviewId: number) => {
    setError(null);
    try {
      const result = await interviewPanelService.join(interviewId);
      setSession({ interviewId, token: result.token, serverUrl: result.serverUrl });
    } catch {
      setError('Could not join — the interview may have been cancelled or is not currently active.');
    }
  };

  if (session) {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>Interview Call</Typography>
        <LiveKitRoomView
          token={session.token}
          serverUrl={session.serverUrl}
          onDisconnected={() => setSession(null)}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>My Interview Panel</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Candidate</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Date &amp; Time</TableCell>
              <TableCell>Panel</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Resume</TableCell>
              <TableCell align="right">Join</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && interviews.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                    No interviews assigned to you yet — this fills in as soon as HR/Admin schedules one with you on the panel.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {interviews.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.candidateName}</TableCell>
                <TableCell>{row.position}</TableCell>
                <TableCell>{formatIst(row.scheduledAt)}</TableCell>
                <TableCell>{row.panelMembers.map(p => p.fullName).join(', ')}</TableCell>
                <TableCell><Chip size="small" label={row.status} color={statusColor(row.status)} /></TableCell>
                <TableCell>
                  {row.resumeUrl
                    ? <MuiLink href={row.resumeUrl} target="_blank" rel="noopener noreferrer">View</MuiLink>
                    : <Typography variant="caption" color="text.secondary">—</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small" variant="contained" startIcon={<VideoCall />}
                    disabled={!row.canJoin}
                    onClick={() => handleJoin(row.id)}
                  >
                    Join
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        After the call, record your evaluation under Recruitment &gt; Interview Feedback.
      </Typography>
    </Box>
  );
};

export default MyInterviewPanel;
